import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface AuthUser {
  readonly id: string
  readonly email: string
  readonly name: string
  readonly role: string
  readonly tenantId: string
  readonly tenantName: string
  /** Granted permission strings as `${resource}:${action}`. */
  readonly permissions: readonly string[]
}

interface AuthState {
  // ─── In-memory only — never persisted ──────────────────────────────────────
  // Security: the short-lived access token must not survive a refresh in storage.
  accessToken: string | null
  isAuthenticated: boolean

  // ─── Persisted — survives refresh ──────────────────────────────────────────
  // tap-api refresh is body-based (no httpOnly cookie), so the refresh token
  // MUST persist to enable silent re-auth across reloads. It is rotated on every
  // refresh. This is the documented trade-off given the API's token design.
  refreshToken: string | null
  user: AuthUser | null

  // ─── Actions ───────────────────────────────────────────────────────────────
  setSession: (tokens: { accessToken: string; refreshToken: string }, user: AuthUser) => void
  setTokens: (tokens: { accessToken: string; refreshToken: string }) => void
  setUser: (user: AuthUser) => void
  clearSession: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      isAuthenticated: false,
      refreshToken: null,
      user: null,
      setSession: ({ accessToken, refreshToken }, user) =>
        set({ accessToken, refreshToken, user, isAuthenticated: true }),
      setTokens: ({ accessToken, refreshToken }) =>
        set({ accessToken, refreshToken, isAuthenticated: true }),
      setUser: (user) => set({ user }),
      clearSession: () =>
        set({ accessToken: null, refreshToken: null, user: null, isAuthenticated: false }),
    }),
    {
      name: 'tap-auth',
      // Persist the refresh token + user only — NEVER the access token.
      partialize: (state): Pick<AuthState, 'refreshToken' | 'user'> => ({
        refreshToken: state.refreshToken,
        user: state.user,
      }),
    },
  ),
)
